import React, { useState, useEffect } from 'react';
import { db } from '../firebase/config';
import { collection, query, orderBy, onSnapshot } from 'firebase/firestore';
import { Bell, Check, RotateCcw, AlertCircle, Sparkles } from 'lucide-react';

const NotificationsScreen: React.FC = () => {
    const [notifications, setNotifications] = useState<any[]>([]);
    const [loading, setLoading] = useState(true);
    const [activeTab, setActiveTab] = useState<'unread' | 'read'>('unread');
    
    // Read state from localStorage
    const [readIds, setReadIds] = useState<string[]>(() => {
        try {
            return JSON.parse(localStorage.getItem('read_notifications') || '[]');
        } catch {
            return [];
        }
    });

    useEffect(() => {
        const q = query(collection(db, 'notifications'), orderBy('createdAt', 'desc'));
        const unsubscribe = onSnapshot(q, (snapshot) => {
            const list: any[] = [];
            snapshot.forEach((doc) => {
                list.push({ id: doc.id, ...doc.data() });
            });
            setNotifications(list);
            setLoading(false);
        }, (error) => {
            console.error('Error fetching notifications in screen:', error);
            setLoading(false);
        });
        return () => unsubscribe();
    }, []);

    const markAsRead = (id: string) => {
        const updated = [...readIds, id];
        setReadIds(updated);
        localStorage.setItem('read_notifications', JSON.stringify(updated));
    };

    const markAsUnread = (id: string) => {
        const updated = readIds.filter(x => x !== id);
        setReadIds(updated);
        localStorage.setItem('read_notifications', JSON.stringify(updated));
    };

    const markAllRead = () => {
        const allIds = notifications.map(n => n.id);
        setReadIds(allIds);
        localStorage.setItem('read_notifications', JSON.stringify(allIds));
    };

    const unreadList = notifications.filter(n => !readIds.includes(n.id));
    const readList = notifications.filter(n => readIds.includes(n.id));

    return (
        <div className="space-y-6">
            {/* Header */}
            <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
                <div>
                    <h1 className="text-2xl font-bold text-gray-900 flex items-center gap-2">
                        System Notifications
                        <span className="text-[10px] font-black bg-emerald-50 text-emerald-600 border border-emerald-200 px-2 py-0.5 rounded-full tracking-wider flex items-center gap-1 uppercase">
                            <Sparkles className="h-3 w-3" /> Live
                        </span>
                    </h1>
                    <p className="text-sm text-gray-500 mt-0.5">Stay informed with system broadcasts, status alerts, and updates.</p>
                </div>
                {unreadList.length > 0 && (
                    <button
                        onClick={markAllRead}
                        className="flex items-center gap-1.5 bg-slate-900 hover:bg-slate-800 text-white text-xs font-semibold px-4 py-2.5 rounded-xl shadow-sm transition-all duration-200"
                    >
                        <Check className="h-4 w-4 text-emerald-400" />
                        Mark All as Read
                    </button>
                )}
            </div>

            {/* Content card */}
            <div className="bg-white rounded-2xl border border-gray-100 shadow-sm overflow-hidden flex flex-col">
                {/* Tabs Toolbar */}
                <div className="flex border-b border-gray-100 bg-gray-50/50 px-4">
                    <button
                        onClick={() => setActiveTab('unread')}
                        className={`flex items-center gap-2 py-4 px-4 text-sm font-semibold border-b-2 transition-all relative ${
                            activeTab === 'unread'
                                ? 'border-emerald-500 text-emerald-600'
                                : 'border-transparent text-gray-500 hover:text-gray-700'
                        }`}
                    >
                        <span>Unread Messages</span>
                        {unreadList.length > 0 && (
                            <span className="bg-emerald-500 text-white text-[10px] font-bold px-2 py-0.5 rounded-full">
                                {unreadList.length}
                            </span>
                        )}
                    </button>
                    <button
                        onClick={() => setActiveTab('read')}
                        className={`flex items-center gap-2 py-4 px-4 text-sm font-semibold border-b-2 transition-all ${
                            activeTab === 'read'
                                ? 'border-emerald-500 text-emerald-600'
                                : 'border-transparent text-gray-500 hover:text-gray-700'
                        }`}
                    >
                        <span>Read Messages</span>
                        {readList.length > 0 && (
                            <span className="bg-gray-200 text-gray-600 text-[10px] font-bold px-2 py-0.5 rounded-full">
                                {readList.length}
                            </span>
                        )}
                    </button>
                </div>

                {/* Notifications list */}
                {loading ? (
                    <div className="flex justify-center items-center py-20">
                        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-emerald-500"></div>
                    </div>
                ) : (
                    <div className="divide-y divide-gray-50">
                        {activeTab === 'unread' ? (
                            unreadList.length === 0 ? (
                                <div className="text-center py-16 text-gray-400">
                                    <Bell className="h-10 w-10 mx-auto mb-3 opacity-30 text-emerald-500" />
                                    <p className="font-semibold text-sm">All caught up!</p>
                                    <p className="text-xs text-gray-400 mt-1">No unread notifications in your inbox.</p>
                                </div>
                            ) : (
                                unreadList.map((n) => {
                                    const typeColor = 
                                        n.type === 'warning' ? 'bg-amber-50 text-amber-600 border border-amber-100' :
                                        n.type === 'success' ? 'bg-emerald-50 text-emerald-600 border border-emerald-100' :
                                        'bg-blue-50 text-blue-600 border border-blue-100';

                                    return (
                                        <div key={n.id} className="p-5 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 hover:bg-slate-50/50 transition-colors">
                                            <div className="flex gap-4 items-start">
                                                <div className={`p-2.5 rounded-xl shrink-0 ${typeColor}`}>
                                                    <Bell className="h-5 w-5" />
                                                </div>
                                                <div>
                                                    <div className="flex items-center gap-2">
                                                        <p className="font-bold text-gray-900 text-base">{n.title}</p>
                                                        <span className="h-2 w-2 bg-emerald-500 rounded-full"></span>
                                                    </div>
                                                    <p className="text-sm text-gray-600 mt-1 leading-relaxed max-w-2xl">{n.description || n.message}</p>
                                                    <span className="text-xs text-gray-400 block mt-2 font-medium">
                                                        Broadcasted: {n.createdAt ? new Date(n.createdAt).toLocaleString() : 'Just now'}
                                                    </span>
                                                </div>
                                            </div>
                                            <button
                                                onClick={() => markAsRead(n.id)}
                                                className="self-start sm:self-auto flex items-center gap-1 bg-emerald-50 hover:bg-emerald-100 text-emerald-700 text-xs font-semibold px-3.5 py-2 rounded-xl transition-colors border border-emerald-200/50 shrink-0"
                                            >
                                                <Check className="h-3.5 w-3.5" />
                                                Mark as Read
                                            </button>
                                        </div>
                                    );
                                })
                            )
                        ) : (
                            readList.length === 0 ? (
                                <div className="text-center py-16 text-gray-400">
                                    <AlertCircle className="h-10 w-10 mx-auto mb-3 opacity-30" />
                                    <p className="font-semibold text-sm">Inbox history is empty</p>
                                    <p className="text-xs text-gray-400 mt-1">Read notifications will appear here for review.</p>
                                </div>
                            ) : (
                                readList.map((n) => {
                                    const typeColor = 
                                        n.type === 'warning' ? 'bg-amber-50 text-amber-600 border border-amber-100' :
                                        n.type === 'success' ? 'bg-emerald-50 text-emerald-600 border border-emerald-100' :
                                        'bg-blue-50 text-blue-600 border border-blue-100';

                                    return (
                                        <div key={n.id} className="p-5 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 hover:bg-slate-50/50 transition-colors opacity-80">
                                            <div className="flex gap-4 items-start">
                                                <div className={`p-2.5 rounded-xl shrink-0 ${typeColor}`}>
                                                    <Bell className="h-5 w-5" />
                                                </div>
                                                <div>
                                                    <p className="font-bold text-gray-800 text-base">{n.title}</p>
                                                    <p className="text-sm text-gray-500 mt-1 leading-relaxed max-w-2xl">{n.description || n.message}</p>
                                                    <span className="text-xs text-gray-400 block mt-2 font-medium">
                                                        Broadcasted: {n.createdAt ? new Date(n.createdAt).toLocaleString() : 'Just now'}
                                                    </span>
                                                </div>
                                            </div>
                                            <button
                                                onClick={() => markAsUnread(n.id)}
                                                className="self-start sm:self-auto flex items-center gap-1 bg-gray-100 hover:bg-gray-200 text-gray-700 text-xs font-semibold px-3.5 py-2 rounded-xl transition-colors border border-gray-200/50 shrink-0"
                                            >
                                                <RotateCcw className="h-3.5 w-3.5" />
                                                Mark Unread
                                            </button>
                                        </div>
                                    );
                                })
                            )
                        )}
                    </div>
                )}
            </div>
        </div>
    );
};

export default NotificationsScreen;
